'use client';

import { ArrowRight, Calculator, ChevronDown, Menu, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

// Profiles live in one place (src/lib/socials) so the nav, footer and any other
// surface can't drift apart as handles are added.
import type { NavLinkContent, SocialLinkContent } from '@/lib/siteContent';
import { pickList } from '@/lib/siteContent';
import { resolveSocials } from '@/lib/socials';

/**
 * Marketing header — ported from the NRM site header design: a sticky frosted
 * white bar that gains a border + shadow on scroll, a logo with a two-line
 * brand block, horizontal nav links with an animated gold underline on hover,
 * a gold CTA pill on the right, and a full-screen dark mobile drawer.
 * Recoloured to ISPS gold/dark and wired to ISPS links.
 */
interface NavLink {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const LINKS: NavLink[] = [
  { label: 'Home', href: '/' },
  {
    // Leadership nests under About (NRM's "NRM Organs" dropdown pattern).
    label: 'About',
    href: '/#about',
    children: [
      { label: 'About ISPS', href: '/#about' },
      { label: 'Leadership & Board of Trustees', href: '/leadership' },
    ],
  },
  { label: 'Benefits', href: '/#benefits' },
  { label: 'Partners', href: '/#partners' },
  { label: 'News', href: '/news' },
];

export function MarketingNav({
  nav,
  socials,
}: {
  nav?: NavLinkContent[] | null;
  socials?: SocialLinkContent[] | null;
}) {
  const links = pickList(
    nav?.map((n) => ({
      label: n.label,
      href: n.href,
      children: n.children?.length
        ? n.children.map((c) => ({ label: c.label, href: c.href }))
        : undefined,
    })),
    LINKS,
  );
  const socialLinks = resolveSocials(socials);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b bg-white/90 backdrop-blur-md backdrop-saturate-150 transition-[border-color,box-shadow] ${
          scrolled
            ? 'border-isps-outline-soft/70 shadow-[0_12px_30px_-22px_rgba(20,19,16,0.4)]'
            : 'border-transparent'
        }`}
      >
        {/* Notice bar — ported from the NRM top strip: dark band, gold accents,
            contacts on the left, quick links + socials on the right. Like NRM,
            it collapses away as you scroll, leaving just the sticky nav row. */}
        <div
          className="grid bg-isps-dark-900 text-white"
          style={{
            gridTemplateRows: scrolled ? '0fr' : '1fr',
            opacity: scrolled ? 0 : 1,
            transition: 'grid-template-rows .3s ease, opacity .3s ease',
          }}
        >
          <div className="overflow-hidden">
          <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-4 px-4 text-[13px] lg:px-8">
            <span className="hidden items-center gap-5 sm:flex">
              <a href="tel:+256701158913" className="whitespace-nowrap hover:text-isps-gold-400">
                Call us <b className="font-semibold text-isps-gold-400">+256 701 158 913</b>
              </a>
              <a href="mailto:info@ispsug.org" className="whitespace-nowrap hover:text-isps-gold-400">
                <b className="font-semibold text-isps-gold-400">info@ispsug.org</b>
              </a>
            </span>

            <div className="ml-auto flex items-center gap-4">
              <a
                href="/#calculator"
                className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-white transition-colors hover:text-isps-gold-400"
              >
                <Calculator className="h-3.5 w-3.5" />
                Savings Calculator
              </a>
              <span className="h-4 w-px bg-white/20" />
              <div className="flex items-center gap-2.5">
                {socialLinks.map(({ Icon, label, href }) => {
                  const external = href.startsWith('http');
                  return (
                    <a
                      key={label}
                      href={href}
                      aria-label={label}
                      {...(external
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                      className="text-white/70 transition-colors hover:text-isps-gold-400"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="mx-auto flex h-[76px] max-w-7xl items-center gap-3 px-4 lg:gap-7 lg:px-8">
          {/* Brand — must be allowed to shrink. It used to be `flex-none`, and
              the tagline is a single 298px line that cannot wrap, so on a
              390px phone the row measured 398px and pushed the menu button
              clean off the right edge: rendered, but unreachable, and the
              mobile nav was effectively gone. `min-w-0` lets the tagline wrap
              to two lines inside the 76px row instead of widening it. */}
          <Link href="/" className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-isps-outline-soft/40">
              <Image src="/isps_logo2.png" alt="ISPS" width={36} height={36} />
            </span>
            <span className="flex min-w-0 flex-col leading-tight">
              <b className="text-[16px] font-extrabold text-isps-dark-900">ISPS</b>
              <span className="mt-0.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-isps-dark-400">
                Informal Workers Social Protection Scheme
              </span>
            </span>
          </Link>

          {/* Desktop nav — animated gold underline on hover */}
          <nav aria-label="Primary" className="ml-auto hidden lg:block">
            <ul className="flex items-center">
              {links.map((l) => (
                <li key={l.href} className="group relative">
                  <Link
                    href={l.href}
                    className="relative flex items-center whitespace-nowrap px-4 py-2.5 text-[15px] font-semibold text-isps-dark-800 transition-colors after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[3px] after:origin-left after:scale-x-0 after:bg-isps-gold-500 after:transition-transform after:duration-300 hover:text-isps-dark-900 hover:after:scale-x-100"
                  >
                    {l.label}
                    {l.children ? (
                      <ChevronDown className="ml-1 h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                    ) : null}
                  </Link>

                  {l.children ? (
                    <div className="invisible absolute left-0 top-[calc(100%+2px)] z-[100] flex min-w-[220px] -translate-y-1.5 flex-col rounded-b border-t-[3px] border-isps-gold-500 bg-white opacity-0 shadow-[0_8px_28px_rgba(0,0,0,0.13)] transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                      {l.children.map((c) => (
                        <Link
                          key={c.href}
                          href={c.href}
                          className="whitespace-nowrap border-b border-[#f0ede6] px-[18px] py-[13px] text-sm font-semibold text-isps-dark-800 last:border-b-0 hover:bg-[#faf9f6]"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </nav>

          {/* Actions */}
          <div className="ml-auto flex flex-none items-center gap-3 lg:ml-0">
            <Link
              href="/login"
              className="hidden text-sm font-semibold text-isps-dark-800 transition-colors hover:text-isps-dark-900 sm:inline"
            >
              Login
            </Link>
            <Link
              href="/signup"
              className="hidden items-center gap-1.5 rounded-full bg-isps-gold-500 px-6 py-2.5 text-sm font-extrabold text-isps-dark-900 transition hover:-translate-y-0.5 hover:bg-isps-gold-600 hover:shadow-[0_10px_24px_rgba(184,134,11,0.35)] sm:inline-flex"
            >
              Join ISPS
              <ArrowRight className="h-4 w-4" />
            </Link>

            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="grid h-11 w-11 flex-none place-items-center text-isps-dark-800 lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — full-screen dark, big links */}
      <div
        className={`fixed inset-0 z-[80] overflow-y-auto bg-isps-dark-900 px-6 pb-12 pt-20 text-white transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] lg:hidden ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          className="absolute right-5 top-5 grid h-11 w-11 place-items-center text-white/80 hover:text-white"
        >
          <X className="h-7 w-7" />
        </button>

        {links.map((l) => (
          <div key={l.href} className="border-b border-white/10">
            {l.children ? (
              <>
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === l.label ? null : l.label)}
                  className="flex w-full items-center justify-between py-4 text-2xl font-bold hover:text-isps-gold-400"
                >
                  {l.label}
                  <ChevronDown
                    className={`h-6 w-6 transition-transform ${expanded === l.label ? 'rotate-180' : ''}`}
                  />
                </button>
                {expanded === l.label ? (
                  <div className="pb-2 pl-4">
                    {l.children.map((c) => (
                      <Link
                        key={c.href}
                        href={c.href}
                        onClick={() => setOpen(false)}
                        className="block py-2 text-lg text-white/70 hover:text-white"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <Link
                href={l.href}
                onClick={() => setOpen(false)}
                className="block py-4 text-2xl font-bold hover:text-isps-gold-400"
              >
                {l.label}
              </Link>
            )}
          </div>
        ))}

        <div className="mt-8 flex flex-col gap-3">
          <Link
            href="/login"
            onClick={() => setOpen(false)}
            className="rounded-full border border-white/25 py-3 text-center text-base font-semibold text-white"
          >
            Login
          </Link>
          <Link
            href="/signup"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-isps-gold-500 py-3 text-center text-base font-extrabold text-isps-dark-900"
          >
            Join ISPS <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </>
  );
}
